import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Paper,
    ProgressBar,
    ScrollableTable,
    ScrollableTableBody,
    ScrollableTableCell,
    ScrollableTableHead,
    ScrollableTableRow,
    ScrollableTableSortableHeaderCells,
    Stack,
    styled,
    Text,
    TextInput,
} from 'shell/components';
import { Placeholder } from 'utils/constants';
import { formatMinutesToReadableTime } from 'utils/helper';

const GradingProgress = ({
    gradingProgress = {},
    predictedGradingTime,
    setPredictedGradingTime,
    calculatedTimeSavedPerSub,
    totalTimeSaved,
    handleCalculateTimeSave,
    handleResetTimeSave,
    sortedGraders = [],
    graderSortState,
    handleSetGraderSortState,
    columns,
    device = 'desktop',
}) => {
    const { total = 0, graded = 0, percentage = 0 } = gradingProgress;

    return (
        <Container spacing={5}>
            <ProgressSection total={total} graded={graded} percentage={percentage} />
            <TimeSaveCalculator
                device={device}
                predictedTime={predictedGradingTime}
                onTimeChange={setPredictedGradingTime}
                onCalculate={handleCalculateTimeSave}
                onReset={handleResetTimeSave}
                savedPerSub={calculatedTimeSavedPerSub}
                totalSaved={totalTimeSaved}
            />
            <GraderTableSection
                graders={sortedGraders}
                columns={columns}
                sortState={graderSortState}
                onSortChange={handleSetGraderSortState}
            />
        </Container>
    );
};

const ProgressSection = ({ total, graded, percentage }) => {
    const { t } = useTranslation('assignment');
    return (
        <Stack spacing={5}>
            <Text variant='p2' weight='medium'>
                {t('submission.grading-progress-label', 'Grading Progress')}
            </Text>
            <Stack spacing={2}>
                <Stack direction='row' justifyContent='space-between' alignItems='baseline'>
                    <Text variant='p2'>{t('submission.submissions-graded-label', 'Submissions Graded')}</Text>
                    <Text variant='p2'>
                        {graded} / {total}
                    </Text>
                </Stack>
                <Stack direction='row' alignItems='center' spacing={4}>
                    <ProgressBar sx={{ flex: 1, width: 'auto' }} value={percentage} />
                    <Text variant='p2'>{percentage}%</Text>
                </Stack>
            </Stack>
        </Stack>
    );
};

const TimeSaveCalculator = ({ predictedTime, onTimeChange, onCalculate, onReset, savedPerSub, totalSaved, device }) => {
    const { t } = useTranslation('assignment');
    return (
        <Stack direction={device === 'mobile' ? 'column' : 'row'} spacing={2.5} alignItems='stretch'>
            <Stack width='100%'>
                <CardBase sx={{ flex: 2 }}>
                    <Stack spacing={2.5} justifyContent='center' width='100%'>
                        <Text variant='p2' color='light'>
                            {t('submission.calculated-time-save-title', 'Calculated Time Save')}
                        </Text>
                        <Stack
                            direction={device !== 'desktop' ? 'column' : 'row'}
                            spacing={10}
                            justifyContent='space-between'
                            height='100%'
                            gap={4}
                        >
                            <Text color='light'>
                                {t(
                                    'submission.calculated-time-save-description',
                                    'How long does it take to grade each file manually?'
                                )}
                            </Text>
                            <Stack spacing={2.5} justifyContent='center' alignItems='flex-start'>
                                <Stack direction='row' spacing={1} alignItems='center'>
                                    <StyledNumberInput
                                        type='number'
                                        value={predictedTime}
                                        onChange={(e) => onTimeChange(e.target.value)}
                                        placeholder={Placeholder.NO_DATA}
                                        aria-label='predicted-time-per-submission'
                                    />
                                    <Text color='light' style={{ whiteSpace: 'nowrap' }}>
                                        {t('common:units.minute-short', 'min')}
                                    </Text>
                                </Stack>
                                <Stack direction='row' spacing={5} sx={{ ml: device !== 'desktop' ? 'auto' : 0 }}>
                                    <Button size='small' variant='borderless' color='neutral' onClick={onReset}>
                                        {t('common:controls.reset', 'Reset')}
                                    </Button>
                                    <Button size='small' variant='borderless' onClick={onCalculate}>
                                        {t('common:controls.calculate', 'Calculate')}
                                    </Button>
                                </Stack>
                            </Stack>
                        </Stack>
                    </Stack>
                </CardBase>
            </Stack>
            <Stack width='100%' direction={'row'} spacing={2.5}>
                <StatCard
                    label={t('submission.time-saved-per-submission', 'Time saved per submission')}
                    value={savedPerSub}
                />
                <StatCard label={t('submission.total-time-saved', 'Total time saved')} value={totalSaved} />
            </Stack>
        </Stack>
    );
};

const StatCard = ({ label, value }) => {
    const { t } = useTranslation();
    return (
        <CardBase>
            <Stack spacing={3} justifyContent='space-between' height='100%'>
                <Text variant='p2' color='light'>
                    {label}
                </Text>
                <Text variant='p1'>
                    {formatMinutesToReadableTime(value, t('units.hour-short', 'hr'), t('units.minute-short', 'min'))}
                </Text>
            </Stack>
        </CardBase>
    );
};

const GraderTableSection = ({ graders, columns, sortState, onSortChange }) => {
    const tableRef = useRef(null);
    const [expandedHeight, setExpandedHeight] = useState(0);
    const [isExpanded, setIsExpanded] = useState(false);
    const { t } = useTranslation('assignment');

    useLayoutEffect(() => {
        if (tableRef.current) {
            setExpandedHeight(tableRef.current.scrollHeight);
        }
    }, [graders]);

    return (
        <Stack spacing={2}>
            <Text variant='p2'>{t('submission.grader-progress', 'Grader Progress')}</Text>
            <TableContainer
                ref={tableRef}
                enableDrag
                expandedHeight={expandedHeight}
                isExpanded={isExpanded}
                aria-label='grader-progress-table'
            >
                <ScrollableTableHead>
                    <ScrollableTableRow>
                        <ScrollableTableSortableHeaderCells
                            columns={columns}
                            sortState={sortState}
                            handleSetSortState={(newState) => onSortChange(newState.orderBy)}
                        />
                    </ScrollableTableRow>
                </ScrollableTableHead>
                <ScrollableTableBody>
                    {graders.map((grader) => (
                        <ScrollableTableRow key={grader.id}>
                            <ScrollableTableCell>{grader.name}</ScrollableTableCell>
                            <ScrollableTableCell>
                                {grader.submissionsGraded} ({grader.submissionsGradedPercentage})
                            </ScrollableTableCell>
                            <ScrollableTableCell>{grader.avgGrade}</ScrollableTableCell>
                            <ScrollableTableCell>{grader.totalTime}</ScrollableTableCell>
                            <ScrollableTableCell>{grader.avgTime}</ScrollableTableCell>
                            <ScrollableTableCell>{grader.aiAccuracy}</ScrollableTableCell>
                        </ScrollableTableRow>
                    ))}
                </ScrollableTableBody>
            </TableContainer>

            {expandedHeight > 236 && (
                <Button
                    variant='borderless'
                    color='primary'
                    onClick={() => setIsExpanded((prev) => !prev)}
                    sx={{ width: 'fit-content' }}
                >
                    {isExpanded
                        ? t('common:controls.show-less', 'Show Less')
                        : t('common:controls.show-more', 'Show More')}
                </Button>
            )}
        </Stack>
    );
};

const Container = styled(Stack)(({ theme }) => ({
    backgroundColor: theme.palette.ensightful.shades[0],
    padding: '1.25rem',
    borderRadius: '12px',
}));

const CardBase = styled(Paper)(({ theme }) => ({
    display: 'flex',
    flex: 1,
    borderRadius: '12px',
    boxShadow: 'none',
    border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
    padding: '16px',
}));

const TableContainer = styled(ScrollableTable, {
    shouldForwardProp: (prop) => prop !== 'isExpanded',
})(({ isExpanded }) => ({
    borderRadius: '12px',
    overflow: 'hidden',
    transition: 'max-height 0.3s ease-out',
    scrollBehavior: 'smooth',
    maxHeight: isExpanded ? '100%' : '236px',

    '& .MuiTableCell-root': {
        minWidth: '176px',
        maxWidth: '176px',
        textAlign: 'left',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
    },
    '& .MuiTableSortLabel-root': {
        textAlign: 'left',
        justifyContent: 'flex-start',
    },
    '& div.MuiBox-root': {
        marginBottom: 0,
    },
}));

const StyledNumberInput = styled(TextInput)({
    width: '7.5rem',
    '& input': {
        textAlign: 'center',
        '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0,
        },
        '-moz-appearance': 'textfield',
    },
});

export default GradingProgress;
